import { UserInfoType } from '@/api/v1/organization/users/getUserInfoApi';
import { useEffect } from 'react';
import { formatPhone } from '@/helpers/formatHelpers';
import { FormikProps, FormikValues } from 'formik';
import { FormValues } from '@/helpers/formHelpers/formHelpers';
import { ResponseErrorType } from '@/types/sharedTypes';
import { HTTP_BAD_REQUEST_STATUS } from '@/api/const';

type UseAssignUserType = Pick<UserInfoType, 'first_name' | 'middle_name' | 'last_name' | 'mobile_phone'>;

export const useAssignUser = <T extends UseAssignUserType>(
  userInfo: T | null,
  formik: FormikProps<FormikValues>,
  onReset: () => void,
  assignedUserId: null | string,
  submittedEmailRequest: boolean,
  isAvailableToAssign: boolean,
  apiError?: ResponseErrorType
) => {
  const setFormValues = (formValues: Partial<FormValues>) => {
    formik.setValues({
      ...formik.values,
      ...formValues,
    });
  };

  const resetUserFormValues = () => {
    setFormValues({
      mobile_phone: '',
      first_name: '',
      middle_name: '',
      last_name: '',
    });
  };

  // Clear the form when previously submitted email value changes
  useEffect(() => {
    if (!submittedEmailRequest) return;

    resetUserFormValues();
    onReset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values?.email]);

  // Fill out the form when user exists and user info is loaded
  useEffect(() => {
    if (!assignedUserId || !userInfo) return;

    const { first_name, middle_name, last_name, mobile_phone } = userInfo;

    setFormValues({
      mobile_phone: formatPhone(mobile_phone),
      first_name,
      middle_name,
      last_name,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignedUserId, userInfo]);

  // Clear the form when user is not available to assign
  useEffect(() => {
    if (!isAvailableToAssign) {
      formik.setErrors({});
      resetUserFormValues();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAvailableToAssign]);

  useEffect(() => {
    if (apiError?.statusCode === HTTP_BAD_REQUEST_STATUS) {
      resetUserFormValues();
      formik.setTouched({}, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiError?.statusCode]);
};
